import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import type {TargetSystem} from '@/types/system'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {routes} from '@/lib/routes'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {TargetSystemControllerSystemsEndpoint} from '@/types/endpoint-contracts'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'

export function EffectiveAccessBySystem() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.analytics.bySystemPageTitle)
    const navigate = useNavigate()
    const {systemTypes, environmentTypes} = useAppConfig()

    const {
        page, pageSize, setPage, setPageSize, pageSizeOptions,
        queryString, searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: TargetSystemControllerSystemsEndpoint,
        defaultSortBy: TargetSystemControllerSystemsEndpoint.sort.DERIVED_DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
    })

    const hasFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
    }, [searchAndFilters])

    // Fetch systems
    const {data: systemsData, isLoading, error} = useQuery({
        queryKey: ['systems', queryString],
        queryFn: () => services.systems.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const systems = systemsData?.content || []
    const totalItems = systemsData?.totalElements || 0
    const totalPages = systemsData?.totalPages || 0

    return (
        <PageLayout
            data-testid="effective-access-by-system-page"
            variant="list"
            icon={CONCEPT_ICON.system}
            loading={isLoading}
            error={error}
            title={i18n.analytics.accessByTargetSystems as string}
            count={totalItems}
            description={searchAndFilters.q ? i18n.analytics.matchingSearch as string : undefined}
            filters={
                <UnifiedSearchBar
                    contract={TargetSystemControllerSystemsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.analytics.searchSystemsPlaceholder as string}
                    testIdNamespace="ea-by-system"
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
            emptyState={
                <TableEmptyState
                    colSpan={4}
                    hasFilters={hasFilters}
                    matchMessage={i18n.analytics.noSystemsMatch}
                    emptyMessage={i18n.analytics.noSystemsFound}
                />
            }
        >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-system">{i18n.analytics.system}</TableHead>
                                <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                                <TableHead data-testid="col-environment">{i18n.analytics.environment}</TableHead>
                                <TableHead data-testid="col-actions">{i18n.common.actions}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {systems.map((system: TargetSystem) => (
                                <ClickableTableRow key={system.id} to={routes.effectiveAccess.systems.detail(system.id)}>
                                    <TableCell className="font-medium">
                                        <TextLink
                                            to={routes.effectiveAccess.systems.detail(system.id)}
                                            testId={`link-system-${system.id}`}
                                            stopPropagation
                                        >
                                            {system.name}
                                        </TextLink>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">
                                            {labelOf(systemTypes, system.systemType)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="secondary">
                                            {labelOf(environmentTypes, system.environment)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                navigate(routes.effectiveAccess.systems.detail(system.id))
                                            }}
                                            data-testid={`btn-view-access-${system.id}`}
                                        >
                                            {i18n.analytics.viewAccess}
                                        </Button>
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                            {systems.length === 0 && (
                                <TableEmptyState
                                    colSpan={4}
                                    hasFilters={hasFilters}
                                    matchMessage={i18n.analytics.noSystemsMatch}
                                    emptyMessage={i18n.analytics.noSystemsFound}
                                />
                            )}
                        </TableBody>
                    </Table>

        </PageLayout>
    )
}
